import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { MapPin, CheckCircle2, Clock, Loader2, ArrowUpRight, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeIn = (delay = 0) => ({
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] },
});

const money = (cents) => `$${(cents / 100).toLocaleString("en-AU", { maximumFractionDigits: 0 })}`;

export const ScreenRates = ({ onBegin, compact = false }) => {
    const [rows, setRows] = useState(null);
    const [error, setError] = useState(null);
    const [query, setQuery] = useState("");
    const [cityFilter, setCityFilter] = useState(null);
    const [availableOnly, setAvailableOnly] = useState(false);

    useEffect(() => {
        axios.get(`${API}/screens/public/map`)
            .then(({ data }) => setRows(data || []))
            .catch(() => setError("Could not load live rates — try again shortly."));
    }, []);

    const allCities = useMemo(
        () => [...new Set((rows || []).map((r) => r.city))].sort(),
        [rows]
    );

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return (rows || []).filter((r) => {
            if (cityFilter && r.city !== cityFilter) return false;
            if (availableOnly && !r.available_this_week) return false;
            if (q && !`${r.name} ${r.location} ${r.city}`.toLowerCase().includes(q)) return false;
            return true;
        });
    }, [rows, query, cityFilter, availableOnly]);

    const byCity = useMemo(() => {
        const map = {};
        filtered.forEach((r) => { (map[r.city] = map[r.city] || []).push(r); });
        return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
    }, [filtered]);

    return (
        <section
            id="rates"
            data-testid="screen-rates-section"
            className={compact ? "relative" : "relative py-14 lg:py-20"}
        >
            <div className={compact ? "" : "mx-auto max-w-7xl px-6 lg:px-10"}>
                <motion.div {...fadeIn(0)} className="mb-10 max-w-3xl">
                    <div className="font-mono-tech text-[11px] uppercase tracking-[0.35em] text-[#FF5A00] mb-6 flex items-center gap-3">
                        <span className="inline-block w-6 h-px bg-[#FF5A00]" />
                        {compact ? "/ Screen rates" : "07 / Screen rates"}
                    </div>
                    <h2 className="font-display font-black tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
                        Every screen.<br />
                        <span className="text-slate-500">One transparent rate.</span>
                    </h2>
                    <p className="mt-6 text-slate-300 max-w-xl text-base">
                        Live daily rates straight from our inventory. Pick your screens, set your dates,
                        pay once — no subscriptions, no lock-ins. Campaigns run in 7-day windows.
                    </p>
                    {onBegin && !compact && (
                        <div className="mt-6 flex items-center gap-3 flex-wrap">
                            <button
                                onClick={() => onBegin()}
                                data-testid="screen-rates-wizard-cta"
                                className="inline-flex items-center gap-2 text-xs font-mono-tech uppercase tracking-widest text-white border border-white/20 hover:border-[#FF5A00] hover:text-[#FF5A00] px-4 py-2.5 transition-colors"
                            >
                                Not sure which to pick? Start the wizard →
                            </button>
                        </div>
                    )}
                </motion.div>

                {rows && rows.length > 0 && (
                    <div className="mb-8 flex flex-wrap items-center gap-3" data-testid="screen-rates-filters">
                        <div className="relative">
                            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                            <input
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search screens or locations…"
                                data-testid="screen-rates-search"
                                className="bg-[#1B2E55]/60 border border-white/10 focus:border-[#FF5A00] text-white pl-9 pr-3 py-2 text-xs w-64 max-w-full placeholder:text-slate-500 transition-colors outline-none"
                            />
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => setCityFilter(null)}
                                data-testid="screen-rates-city-all"
                                className={`px-3 py-1.5 text-[10px] font-mono-tech uppercase tracking-widest border transition-colors ${!cityFilter ? "border-[#FF5A00] bg-[#FF5A00]/10 text-[#FF5A00]" : "border-white/15 text-slate-400 hover:text-white"}`}
                            >All</button>
                            {allCities.map((c) => (
                                <button
                                    key={c}
                                    onClick={() => setCityFilter(cityFilter === c ? null : c)}
                                    data-testid={`screen-rates-city-${c}`}
                                    className={`px-3 py-1.5 text-[10px] font-mono-tech uppercase tracking-widest border transition-colors ${cityFilter === c ? "border-[#FF5A00] bg-[#FF5A00]/10 text-[#FF5A00]" : "border-white/15 text-slate-400 hover:text-white"}`}
                                >{c}</button>
                            ))}
                        </div>
                        <button
                            onClick={() => setAvailableOnly((v) => !v)}
                            data-testid="screen-rates-available-toggle"
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-mono-tech uppercase tracking-widest border transition-colors ${availableOnly ? "border-emerald-400/60 bg-emerald-400/10 text-emerald-300" : "border-white/15 text-slate-400 hover:text-white"}`}
                        >
                            <CheckCircle2 size={11} /> Available now
                        </button>
                        <span className="text-[10px] font-mono-tech uppercase tracking-[0.25em] text-slate-500" data-testid="screen-rates-count">
                            {filtered.length} screen{filtered.length === 1 ? "" : "s"}
                        </span>
                    </div>
                )}

                {error ? (
                    <div className="border border-[#FF5A00]/40 p-6 text-sm text-slate-300" data-testid="screen-rates-error">{error}</div>
                ) : !rows ? (
                    <div className="flex items-center gap-2 text-sm text-slate-400 py-8" data-testid="screen-rates-loading">
                        <Loader2 size={16} className="animate-spin text-[#FF5A00]" /> Loading live rates…
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="border border-white/10 p-6 text-sm text-slate-400" data-testid="screen-rates-no-match">
                        No screens match those filters — try widening your search.
                    </div>
                ) : (
                    <div className="space-y-8" data-testid="screen-rates-list">
                        {byCity.map(([city, screens], ci) => (
                            <motion.div key={city} {...fadeIn(0.05 * ci)}>
                                <div className="flex items-center gap-2 mb-3">
                                    <MapPin size={14} className="text-[#FF5A00]" />
                                    <span className="font-mono-tech text-[11px] uppercase tracking-[0.35em] text-slate-400">{city}</span>
                                    <span className="flex-1 h-px bg-white/10" />
                                </div>
                                <div className="grid grid-cols-1 gap-px bg-white/5 border border-white/10">
                                    {screens.map((s) => (
                                        <div key={s.id}
                                            data-testid={`rate-row-${s.id}`}
                                            className="bg-[#1B2E55]/60 hover:bg-[#1B2E55] transition-colors px-5 py-4 grid grid-cols-2 sm:grid-cols-[1fr_auto_auto_auto] items-center gap-x-6 gap-y-2">
                                            <div className="min-w-0 col-span-2 sm:col-span-1">
                                                <div className="text-white text-sm font-medium truncate">{s.name}</div>
                                                <div className="text-[10px] font-mono-tech uppercase tracking-widest text-slate-500 truncate">{s.location}</div>
                                            </div>
                                            <div className="text-left sm:text-right">
                                                <div className="font-display font-black tracking-tighter text-xl text-white">{money(s.daily_rate_cents)}<span className="text-[10px] font-mono-tech uppercase tracking-widest text-slate-500 ml-1">/ day</span></div>
                                                <div className="text-[10px] font-mono-tech uppercase tracking-widest text-slate-500">{money(s.daily_rate_cents * 7)} / 7-day run</div>
                                            </div>
                                            <div>
                                                {s.available_this_week ? (
                                                    <span className="inline-flex items-center gap-1.5 border border-emerald-400/40 text-emerald-400 px-2.5 py-1 text-[10px] font-mono-tech uppercase tracking-widest">
                                                        <CheckCircle2 size={11} /> Available
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1.5 border border-[#FF5A00]/40 text-[#FF5A00] px-2.5 py-1 text-[10px] font-mono-tech uppercase tracking-widest">
                                                        <Clock size={11} /> Waitlist
                                                    </span>
                                                )}
                                            </div>
                                            <button
                                                onClick={() => onBegin && onBegin(s)}
                                                data-testid={`rate-book-${s.id}`}
                                                className="inline-flex items-center justify-center gap-1.5 bg-[#FF5A00] hover:bg-[#E65200] text-white px-4 py-2 text-[11px] font-semibold uppercase tracking-widest transition-colors">
                                                Book <ArrowUpRight size={12} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}

                <p className="mt-6 text-[10px] font-mono-tech uppercase tracking-[0.25em] text-slate-500">
                    / Rates exclude GST · Availability shown for the next 7 days · Screens are held for 20 minutes at checkout
                </p>
            </div>
        </section>
    );
};
